<template>
  <div class="printer-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">Printer Settings</span>
        <span class="page-subtitle">Configure your receipt printer</span>
      </div>
    </div>

    <div class="section-pad">

      <!-- ── Connection status card ── -->
      <div class="status-card" :class="'status-card--' + connectionStateKey" v-if="printer_type">

        <div class="status-left">
          <div class="status-icon-wrap">
            <svg v-if="connection_status === 'checking'" class="spin-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.3"/></svg>
            <svg v-else-if="connection_status === 'connected'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else-if="connection_status === 'disconnected'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div class="status-info">
            <div class="status-label">{{ statusLabel }}</div>
            <div class="status-detail" v-if="printer_type === 'network' && ip_address">
              {{ ip_address }}{{ port ? ':' + port : '' }}
            </div>
            <div class="status-detail" v-if="connection_status === 'disconnected' && disconnect_reason">
              {{ disconnect_reason }}
            </div>
            <div class="status-detail" v-if="last_checked">Last checked {{ last_checked }}</div>
          </div>
        </div>

        <button
          class="reconnect-btn"
          :class="{ 'reconnect-btn--spinning': connection_status === 'checking' }"
          :disabled="connection_status === 'checking'"
          @click="checkConnection"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.3"/></svg>
          {{ connection_status === 'checking' ? 'Testing…' : 'Test' }}
        </button>
      </div>

      <!-- Troubleshooting panel — shown only when disconnected -->
      <div class="trouble-card" v-if="connection_status === 'disconnected'">
        <div class="trouble-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Troubleshooting
        </div>
        <ul class="trouble-list" v-if="printer_type === 'network'">
          <li>Check the printer is powered on and its power light is solid.</li>
          <li>Confirm the printer is connected to the same Wi-Fi network as this device.</li>
          <li>Verify the IP address below is correct — it may have changed if the printer was restarted.</li>
          <li>Ensure no firewall or VLAN is blocking port {{ port || 9100 }}.</li>
          <li>Try rebooting the printer, then tap <strong>Test</strong> again.</li>
        </ul>
        <ul class="trouble-list" v-if="printer_type === 'bluetooth'">
          <li>Make sure Bluetooth is enabled on this device.</li>
          <li>Confirm the printer is powered on and in pairing mode.</li>
          <li>Go to this device's Bluetooth settings and check the printer is paired.</li>
          <li>If it was previously paired, try forgetting and re-pairing the printer.</li>
          <li>Tap <strong>Test</strong> to retry the connection.</li>
        </ul>
      </div>

      <!-- Settings form -->
      <div class="form-card">
        <div class="form-card-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
          Connection
        </div>

        <div class="fields-wrap">

          <div class="field-group">
            <label class="field-label">Printer Type</label>
            <div class="select-wrap">
              <svg class="select-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              <select class="field-select" v-model="printer_type" @change="connection_status = 'unknown'">
                <option value="">Select printer type</option>
                <option v-for="p in printers" :key="p" :value="p">{{ p.charAt(0).toUpperCase() + p.slice(1) }}</option>
              </select>
              <i class="ft-chevron-down chevron-icon"></i>
            </div>
          </div>

          <div class="field-group" v-if="printer_type === 'network'">
            <label class="field-label">IP Address</label>
            <div class="input-wrap">
              <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
              <input type="text" class="field-input" placeholder="e.g. 192.168.1.100" v-model="ip_address" @change="connection_status = 'unknown'" />
            </div>
          </div>

          <div class="field-group" v-if="printer_type === 'network'">
            <label class="field-label">Port</label>
            <div class="input-wrap">
              <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              <input type="number" class="field-input" placeholder="e.g. 9100" v-model="port" @change="connection_status = 'unknown'" />
            </div>
            <p class="field-hint">Default port for most network printers is 9100.</p>
          </div>

        </div>
      </div>

      <!-- Paper settings -->
      <div class="form-card">
        <div class="form-card-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Paper
        </div>
        <div class="fields-wrap">
          <div class="field-group">
            <label class="field-label">Paper Width (mm)</label>
            <div class="paper-options">
              <button class="paper-btn" :class="{ 'paper-btn--active': String(paper_size) === '57' }" type="button" @click="paper_size = 57">57 mm</button>
              <button class="paper-btn" :class="{ 'paper-btn--active': String(paper_size) === '80' }" type="button" @click="paper_size = 80">80 mm</button>
              <button class="paper-btn" :class="{ 'paper-btn--active': String(paper_size) !== '57' && String(paper_size) !== '80' }" type="button" @click="customPaper = true">Custom</button>
            </div>
            <div class="input-wrap" style="margin-top:10px" v-if="String(paper_size) !== '57' && String(paper_size) !== '80'">
              <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              <input type="number" class="field-input" placeholder="Enter width in mm" v-model="paper_size" />
            </div>
          </div>
        </div>
      </div>

      <!-- Save button -->
      <button class="save-btn" @click="save_settings">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        Save Settings
      </button>

    </div>
  </div>
</template>

<script>
import swal from 'sweetalert'
import moment from 'moment'

export default {
  name: 'printer',
  data: function () {
    return {
      user: JSON.parse(window.localStorage.getItem('user')),
      printer_type: '',
      ip_address: '',
      port: 9100,
      paper_size: 57,
      printers: ['bluetooth', 'network'],
      customPaper: false,
      connection_status: 'unknown', // 'unknown' | 'checking' | 'connected' | 'disconnected'
      disconnect_reason: '',
      last_checked: ''
    }
  },
  computed: {
    connectionStateKey: function () {
      return this.connection_status
    },
    statusLabel: function () {
      var labels = {
        unknown:      'Not tested yet',
        checking:     'Testing connection…',
        connected:    'Connected',
        disconnected: 'Disconnected'
      }
      return labels[this.connection_status] || 'Unknown'
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    var that = this
    if (window.localStorage.getItem('printer_type') != null) that.printer_type = window.localStorage.getItem('printer_type')
    if (window.localStorage.getItem('ip_address')   != null) that.ip_address   = window.localStorage.getItem('ip_address')
    if (window.localStorage.getItem('port')         != null) that.port         = window.localStorage.getItem('port')
    if (window.localStorage.getItem('paper_size')   != null) that.paper_size   = window.localStorage.getItem('paper_size')

    // Auto-test on load if a printer is already configured
    if (that.printer_type) {
      that.checkConnection()
    }
  },
  methods: {
    checkConnection: function () {
      var that = this
      that.disconnect_reason = ''
      that.connection_status = 'checking'

      // ── Network probe ──────────────────────────────────────
      if (that.printer_type === 'network') {
        if (!that.ip_address) {
          that.connection_status = 'disconnected'
          that.disconnect_reason = 'No IP address configured.'
          that.last_checked = moment().format('h:mm A')
          return
        }

        var url = 'http://' + that.ip_address + ':' + (that.port || 9100) + '/'
        var controller = typeof AbortController !== 'undefined' ? new AbortController() : null
        var timeoutId = setTimeout(function () {
          if (controller) controller.abort()
        }, 5000)

        var fetchOptions = { mode: 'no-cors', cache: 'no-store' }
        if (controller) fetchOptions.signal = controller.signal

        fetch(url, fetchOptions)
          .then(function () {
            clearTimeout(timeoutId)
            that.connection_status = 'connected'
            that.last_checked = moment().format('h:mm A')
          })
          .catch(function (err) {
            clearTimeout(timeoutId)
            that.last_checked = moment().format('h:mm A')
            if (err && err.name === 'AbortError') {
              that.connection_status = 'disconnected'
              that.disconnect_reason = 'Request timed out — printer did not respond within 5 seconds.'
            } else {
              // A non-timeout fetch error usually means the host refused or is unreachable.
              // With no-cors, a quick CORS/refused error still means the host is reachable.
              var msg = (err && err.message) ? err.message.toLowerCase() : ''
              if (msg.indexOf('network') > -1 || msg.indexOf('failed') > -1) {
                that.connection_status = 'disconnected'
                that.disconnect_reason = 'Network request failed — check that ' + that.ip_address + ' is reachable.'
              } else {
                // Got some kind of response (even CORS refusal) = host is up
                that.connection_status = 'connected'
              }
            }
          })
        return
      }

      // ── Bluetooth probe ────────────────────────────────────
      if (that.printer_type === 'bluetooth') {
        // Check device online state as a first signal
        if (!navigator.onLine) {
          that.connection_status = 'disconnected'
          that.disconnect_reason = 'This device has no network connection.'
          that.last_checked = moment().format('h:mm A')
          return
        }

        // If the Cordova Bluetooth Serial plugin is available, use it
        if (window.bluetoothSerial) {
          window.bluetoothSerial.isEnabled(
            function () {
              // BT on — list connected devices
              window.bluetoothSerial.list(
                function (devices) {
                  that.last_checked = moment().format('h:mm A')
                  if (devices && devices.length > 0) {
                    that.connection_status = 'connected'
                  } else {
                    that.connection_status = 'disconnected'
                    that.disconnect_reason = 'Bluetooth is on but no paired printer was found.'
                  }
                },
                function () {
                  that.connection_status = 'disconnected'
                  that.disconnect_reason = 'Could not list Bluetooth devices.'
                  that.last_checked = moment().format('h:mm A')
                }
              )
            },
            function () {
              that.connection_status = 'disconnected'
              that.disconnect_reason = 'Bluetooth is turned off on this device. Please enable it in Settings.'
              that.last_checked = moment().format('h:mm A')
            }
          )
        } else {
          // Plugin not available (browser / dev mode) — indicate unknown
          that.connection_status = 'disconnected'
          that.disconnect_reason = 'Bluetooth probe requires the app to run on a physical device.'
          that.last_checked = moment().format('h:mm A')
        }
        return
      }

      // Fallback — nothing configured
      that.connection_status = 'unknown'
      that.last_checked = moment().format('h:mm A')
    },

    save_settings: function () {
      var that = this
      window.localStorage.setItem('printer_type', that.printer_type)
      window.localStorage.setItem('ip_address',   that.ip_address)
      window.localStorage.setItem('port',         that.port)
      window.localStorage.setItem('paper_size',   that.paper_size)
      swal({ text: 'Settings saved successfully!', icon: 'success', button: 'Ok' }).then(function () {
        if (that.printer_type) that.checkConnection()
      })
    }
  }
}
</script>

<style scoped>
.printer-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s;
}
.back-btn:hover { background: rgba(255,255,255,0.25); }

.header-info { display: flex; flex-direction: column; gap: 3px; }
.page-title   { font-size: 1.05rem; font-weight: 700; color: #fff; }
.page-subtitle{ font-size: 0.73rem; color: rgba(255,255,255,0.65); font-weight: 500; }

/* ── Section ── */
.section-pad {
  padding: 110px 16px 0;
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Status card ── */
.status-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  border: 2px solid transparent;
  transition: background 0.2s, border-color 0.2s;
}

.status-card--unknown     { background: #f8fafc; border-color: #e2e8f0; }
.status-card--checking    { background: #eff6ff; border-color: #bfdbfe; }
.status-card--connected   { background: #f0fdf4; border-color: #86efac; }
.status-card--disconnected{ background: #fef2f2; border-color: #fca5a5; }

.status-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.status-icon-wrap {
  width: 42px; height: 42px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.status-card--unknown      .status-icon-wrap { background: #e2e8f0;  color: #64748b; }
.status-card--checking     .status-icon-wrap { background: #dbeafe;  color: #2563eb; }
.status-card--connected    .status-icon-wrap { background: #dcfce7;  color: #16a34a; }
.status-card--disconnected .status-icon-wrap { background: #fee2e2;  color: #dc2626; }

.status-info { min-width: 0; }

.status-label {
  font-size: 0.95rem;
  font-weight: 700;
}
.status-card--unknown      .status-label { color: #475569; }
.status-card--checking     .status-label { color: #1d4ed8; }
.status-card--connected    .status-label { color: #15803d; }
.status-card--disconnected .status-label { color: #dc2626; }

.status-detail {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Reconnect button ── */
.reconnect-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  border: 1.5px solid currentColor;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}
.status-card--unknown      .reconnect-btn { color: #475569; }
.status-card--checking     .reconnect-btn { color: #2563eb; opacity: 0.6; cursor: not-allowed; }
.status-card--connected    .reconnect-btn { color: #15803d; }
.status-card--disconnected .reconnect-btn { color: #dc2626; }

.reconnect-btn:hover:not(:disabled) { background: rgba(0,0,0,0.05); }
.reconnect-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Spinner animation on the reconnect icon while checking */
.status-card--checking .reconnect-btn svg,
.spin-icon {
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Troubleshooting card ── */
.trouble-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid #fca5a5;
  overflow: hidden;
}

.trouble-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  background: #fef2f2;
  border-bottom: 1px solid #fca5a5;
  font-size: 0.78rem;
  font-weight: 700;
  color: #dc2626;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.trouble-list {
  margin: 0;
  padding: 14px 18px 14px 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: disc;
}

.trouble-list li {
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.5;
}

.trouble-list li strong { color: #0f172a; }

/* ── Form cards ── */
.form-card {
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid #e8edf2;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e8edf2;
  font-size: 0.78rem;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.9px;
}
.form-card-header svg { color: #1a587b; flex-shrink: 0; }

.fields-wrap {
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-group { display: flex; flex-direction: column; gap: 6px; }

.field-label {
  font-size: 0.73rem; font-weight: 700;
  color: #64748b; text-transform: uppercase; letter-spacing: 0.7px;
}

.field-hint { font-size: 0.73rem; color: #94a3b8; margin: 4px 0 0; line-height: 1.4; }

.select-wrap { position: relative; display: flex; align-items: center; }
.select-icon  { position: absolute; left: 13px; color: #94a3b8; pointer-events: none; flex-shrink: 0; }
.field-select {
  width: 100%; padding: 11px 38px 11px 38px;
  border: 1.5px solid #e8edf2; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; background: #f8fafc;
  appearance: none; cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.field-select:focus { outline: none; border-color: #1a587b; box-shadow: 0 0 0 3px rgba(26,88,123,0.1); background: #fff; }
.chevron-icon { position: absolute; right: 12px; color: #94a3b8; pointer-events: none; font-size: 0.8rem; }

.input-wrap  { position: relative; display: flex; align-items: center; }
.input-icon  { position: absolute; left: 13px; color: #94a3b8; pointer-events: none; }
.field-input {
  width: 100%; padding: 11px 14px 11px 38px;
  border: 1.5px solid #e8edf2; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.field-input:focus { outline: none; border-color: #1a587b; box-shadow: 0 0 0 3px rgba(26,88,123,0.1); background: #fff; }
.field-input::placeholder { color: #cbd5e1; }

.paper-options { display: flex; gap: 8px; }
.paper-btn {
  flex: 1; padding: 10px 8px;
  border-radius: 10px; border: 1.5px solid #e8edf2;
  background: #f8fafc; font-size: 0.85rem; font-weight: 600; color: #64748b;
  cursor: pointer; transition: all 0.15s;
}
.paper-btn:hover { border-color: #1a587b; color: #1a587b; }
.paper-btn--active { background: #1a587b; border-color: #1a587b; color: #fff; }

/* ── Save button ── */
.save-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  border: none; color: #fff;
  font-size: 0.95rem; font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  letter-spacing: 0.2px;
}
.save-btn:hover  { opacity: 0.92; }
.save-btn:active { transform: scale(0.98); }
</style>
